"use client"

import { logger } from "@/lib/logger"
import NextLink from "next/link"
import { useRouter } from "next/navigation"
import { useEffect, useRef } from "react"
import { z } from "zod"

type PrefetchImage = {
    srcset: string
    sizes: string
    src: string
    alt: string
    loading: string
}

function sleep(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms))
}

async function prefetchImages(href: string) {
    if (!href.startsWith("/") || href.startsWith("/order") || href === "/") {
        return []
    }
    const url = new URL(href, window.location.href)
    const imageResponse = await fetch(`/api/prefetch-images${url.pathname}`, {
        priority: "low",
    })
    // only throw in dev
    if (!imageResponse.ok && process.env.NODE_ENV === "development") {
        throw new Error("Failed to prefetch images")
    }
    const data = await imageResponse.json()
    const parsedData = z
        .object({
            images: z.array(
                z.object({
                    srcset: z.string(),
                    sizes: z.string(),
                    src: z.string(),
                    alt: z.string(),
                    loading: z.string(),
                }),
            ),
        })
        .parse(data)
    return parsedData.images as PrefetchImage[]
}

const seen = new Set<string>()
const imageCache = new Map<string, PrefetchImage[]>()

export const FastLink: typeof NextLink = (({ children, ...props }) => {
    const linkRef = useRef<HTMLAnchorElement>(null)
    const router = useRouter()
    let prefetchTimeout: NodeJS.Timeout | null = null

    useEffect(() => {
        if (props.prefetch === false) return

        const linkElement = linkRef.current
        if (!linkElement) return

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0]
                if (entry && entry.isIntersecting) {
                    // eslint-disable-next-line react-hooks/exhaustive-deps
                    prefetchTimeout = setTimeout(async () => {
                        router.prefetch(String(props.href))
                        await sleep(0)

                        if (!imageCache.has(String(props.href))) {
                            void prefetchImages(String(props.href)).then(
                                (images) => {
                                    imageCache.set(String(props.href), images)
                                },
                                logger.error,
                            )
                        }

                        observer.unobserve(entry.target)
                    }, 300)
                } else if (prefetchTimeout) {
                    clearTimeout(prefetchTimeout)
                    prefetchTimeout = null
                }
            },
            { rootMargin: "0px", threshold: 0.1 },
        )

        observer.observe(linkElement)

        return () => {
            observer.disconnect()
            if (prefetchTimeout) {
                clearTimeout(prefetchTimeout)
            }
        }
    }, [props.href, props.prefetch])

    return (
        <NextLink
            ref={linkRef}
            prefetch={false}
            onMouseEnter={() => {
                router.prefetch(String(props.href))
                const images = imageCache.get(String(props.href)) || []
                for (const image of images) {
                    prefetchImage(image)
                }
            }}
            onMouseDown={(e) => {
                const url = new URL(String(props.href), window.location.href)
                if (
                    url.origin === window.location.origin &&
                    e.button === 0 &&
                    !e.altKey &&
                    !e.ctrlKey &&
                    !e.metaKey &&
                    !e.shiftKey
                ) {
                    e.preventDefault()
                    router.push(String(props.href))
                }
            }}
            {...props}
        >
            {children}
        </NextLink>
    )
}) as typeof NextLink

function prefetchImage(image: PrefetchImage) {
    if (image.loading === "lazy" || seen.has(image.srcset)) {
        return
    }
    const img = new Image()
    img.decoding = "async"
    img.fetchPriority = "low"
    img.sizes = image.sizes
    seen.add(image.srcset)
    img.srcset = image.srcset
    img.src = image.src
    img.alt = image.alt
}
